import React, { FC, useMemo } from 'react'
import { FormControl, FormHelperText, Grid, InputLabel, MenuItem, Select } from '@mui/material'
import { useFormContext } from 'react-hook-form'
import { makeStyles } from '@mui/styles'
import clsx from 'clsx'
import { SelectArrow } from '../SelectArrow'
import capitalizeFirstLetter from '../../utils/capitalizeFirstLetter'
import { Colors } from '../../theme'

const useStyles = makeStyles({
  inputContainer: {
    width: ({ width }: InputSelectProps) => width,
    marginTop: ({ mt }: InputSelectProps) => mt,
    marginBottom: ({ mb }: InputSelectProps) => mb,
    marginLeft: ({ ml }: InputSelectProps) => ml,
    marginRight: ({ mr }: InputSelectProps) => mr,
    paddingLeft: ({ pl }: InputSelectProps) => pl,
    maxHeight: 63,
    '& .MuiSelect-select': {
      color: `rgba(255,255,255,0.6)`,
      fontSize: 14,
      padding: ({ padding }: InputSelectProps) => padding,
    },
    '& .Mui-disabled': {
      '& .MuiSelect-select': {
        color: Colors.white,
        opacity: '30%',
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(255,255,255,0.4)',
      },
      '& .MuiOutlinedInput-input:-webkit-autofill': {
        '-webkit-box-shadow': 'none',
      },
      '& :-webkit-autofill': {
        '-webkit-background-clip': 'text',
      },
    },
  },
  inputContainerNoLabel: {
    maxHeight: 40,
  },
  label: {
    fontSize: '18px',
    marginBottom: '-3px',
    color: '#FFFFFF',
    opacity: '60%',
  },
  formControl: {
    width: ({ width }: InputSelectProps) => width,
    height: '40px',
    color: '#FFFFFF',
    boxSizing: 'border-box',
    '& .MuiOutlinedInput-input:-webkit-autofill': {
      '-webkit-box-shadow': 'none',
    },
    '& :-webkit-autofill': {
      transition: 'background-color 5000s ease-in-out 0s',
    },
  },
  selectField: {
    color: '#FFFFFF',
  },
  disabled: {
    opacity: '60%',
  },
})

export interface IEnums {
  value: string
  label: string
}

interface InputSelectProps {
  enums?: IEnums[]
  labelName?: string
  inputName: string
  placeholder?: string
  width: number | string
  mt?: number | string
  mb?: number | string
  ml?: number | string
  mr?: number | string
  pl?: number | string
  padding?: number | string
  disable?: boolean | undefined
  enumDisableValue?: string
  defaultValue?: string | number
}

const selectArr = [
  { id: 1, city: 'Num 1' },
  { id: 2, city: 'Num 2' },
  { id: 3, city: 'Num 3' },
]

export const FilterSelect: FC<InputSelectProps> = ({
  enums,
  labelName,
  inputName,
  placeholder,
  width,
  pl,
  mb,
  ml,
  mr,
  mt,
  padding,
  disable,
  enumDisableValue,
  defaultValue = '',
}) => {
  const styles = useStyles({
    width,
    mt,
    mb,
    pl,
    placeholder,
    inputName,
    ml,
    mr,
    padding,
    disable,
    defaultValue,
  })
  const {
    register,
    formState: { errors },
  } = useFormContext()
  const error = errors[inputName]

  const errorMessage = useMemo(() => {
    const message = error?.message.replace(inputName, '')
    return capitalizeFirstLetter(message)
  }, [error?.message, inputName])

  return (
    <Grid
      container
      item
      className={clsx(!labelName ? styles.inputContainerNoLabel : '', styles.inputContainer)}
      direction={'column'}
      wrap={'nowrap'}
    >
      {labelName && (
        <Grid item>
          <InputLabel shrink htmlFor={inputName} className={styles.label}>
            {labelName}
          </InputLabel>
        </Grid>
      )}
      <Grid item>
        <FormControl className={styles.formControl} variant={'outlined'} size={'small'}>
          <Select
            id={inputName}
            displayEmpty
            IconComponent={SelectArrow}
            defaultValue={defaultValue}
            disabled={disable}
            {...register(inputName)}
            error={!!error}
            className={styles.selectField}
            inputProps={{ 'aria-label': 'Without label' }}
          >
            {placeholder && (
              <MenuItem className={styles.disabled} value="" key={'empty'}>
                {placeholder}
              </MenuItem>
            )}

            {enums &&
              enums.map((data: IEnums, index) => (
                <MenuItem key={index + data.value} disabled={data.value === enumDisableValue} value={data.value}>
                  {data.label}
                </MenuItem>
              ))}

            {!enums &&
              selectArr.map((el) => (
                <MenuItem key={el.id} value={el.city}>
                  {el.city}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item>
        {error && (
          <FormHelperText error id="component-error-text">
            {errorMessage}
          </FormHelperText>
        )}
      </Grid>
    </Grid>
  )
}
